import heroTurfImage from "./arena-assets/hero-turf.jpg";
import badmintonImage from "./arena-assets/facility-badminton.jpg";
import cricketImage from "./arena-assets/facility-cricket.jpg";
import tennisImage from "./arena-assets/facility-tennis.jpg";
import pickleballImage from "./arena-assets/facility-pickleball.jpg";
import poolImage from "./arena-assets/facility-pool.jpg";

const cleanText = (value) => String(value ?? "").trim();

export const ARENA_REFERENCE_IMAGES = Object.freeze({
  hero: heroTurfImage,
  football: heroTurfImage,
  badminton: badmintonImage,
  cricket: cricketImage,
  tennis: tennisImage,
  pickleball: pickleballImage,
  swimming: poolImage,
  howItWorks: badmintonImage,
  location: cricketImage,
  about: Object.freeze([
    heroTurfImage,
    badmintonImage,
    poolImage,
  ]),
});

export const ARENA_REFERENCE_VENUE = Object.freeze({
  name: "BoxArena",
  tagline: "Where champions train. Where friends play.",
  city: "Bengaluru, Karnataka",
  address: "142, Sarjapur Main Road, Bellandur, Bengaluru 560103",
  phone: "+91 80 4567 8900",
  email: "hello@ongroundarena.com",
  hours: "Open 6:00 AM – 11:00 PM · Every day",
  rating: 4.9,
  reviewCount: 1284,
  heroHeadline: "Where champions train. Where friends play.",
  heroSubheadline:
    "Six sports, one premium arena in the heart of Bengaluru. Book a turf, court or lane in under 30 seconds.",
  aboutLead:
    "Onground Arena started in 2021 when three weekend footballers got tired of chasing broken WhatsApp bookings on patchy turfs. Today we host over 40,000 bookings a year across six sports — and we're just getting started.",
  aboutBody:
    "Every court is designed by athletes, maintained daily, and priced fairly. We think sports infrastructure should feel as good as a great café — and we're building it, one turf at a time.",
});

export const ARENA_REFERENCE_COPY = Object.freeze({
  hoursPrefix: "Open",
  hoursSuffix: "· Every day",
  heroBadge: "Now open on Sarjapur Road",
  groundsEyebrow: "Grounds",
  groundsTitle: "Pick your ground.",
  groundsDescription: "Six grounds. Real availability. Book by the hour.",
  groundCapacitySuffix: "courts",
  pricePrefix: "from",
  priceOnRequestLabel: "Price on request",
  groundBookLabel: "Book",
  emptyGroundsLabel: "Ground details will be available soon.",
  bookingEyebrow: "Booking in 30 seconds",
  bookingTitle: "A booking flow that actually respects your time.",
  bookingDescription:
    "Three taps to a confirmed slot. No calls. No back-and-forth. Free cancellation up to 6 hours.",
  bookingStepOneTitle: "Choose your ground",
  bookingStepOneDescription: "Filter by turf size, surface, or price.",
  bookingStepTwoTitle: "Pick a slot",
  bookingStepTwoDescription:
    "Real-time availability. Peak-hour pricing shown up-front.",
  bookingStepThreeTitle: "Pay & play",
  bookingStepThreeDescription:
    "UPI, cards, wallets. Confirmation on WhatsApp.",
  bookingButtonLabel: "Pick your ground",
  testimonialsEyebrow: "Loved by 12,000+ players",
  testimonialsTitle: "The word on the pitch.",
  aboutEyebrow: "Our story",
  aboutTitle: "Bengaluru deserved a better place to play.",
  locationEyebrow: "Location",
  locationContactLabel: "Contact",
  contactEyebrow: "Contact",
  contactTitle: "Say hello. We'll reply fast.",
  contactDescription:
    "Bookings, memberships, corporate takeovers, or tournament partnerships — drop us a line.",
  contactOpenLabel: "Open",
  contactOpenDescription:
    "Book online 24/7 · Reception on-site 6 AM – 11 PM",
  faqEyebrow: "FAQ",
  faqTitle: "Questions we get a lot.",
  footerQuickLinksTitle: "Quick links",
  footerBookLabel: "Book a slot",
  footerAboutLabel: "About us",
  footerContactLabel: "Contact & FAQ",
  footerStartBookingLabel: "Book a slot",
  footerVisitTitle: "Visit us",
  footerCopyrightSuffix: "All rights reserved.",
  footerPoweredByText: "Powered by VenueSites by Onground",
});

export const ARENA_REFERENCE_TESTIMONIALS = Object.freeze([
  Object.freeze({
    name: "Aditi R.",
    role: "Weekend footballer",
    text: "Booking a turf used to take five WhatsApp messages. Now it's three taps. The pitch is world-class.",
    rating: 5,
  }),
  Object.freeze({
    name: "Rahul M.",
    role: "Cricket coach",
    text: "We run all our academy sessions here. The turf is spotless and the staff is always ten steps ahead.",
    rating: 5,
  }),
  Object.freeze({
    name: "Priya K.",
    role: "Badminton regular",
    text: "Wooden courts, spotless changing rooms, and I can see my slots for the week at a glance. Perfect.",
    rating: 5,
  }),
]);

export const ARENA_REFERENCE_FAQS = Object.freeze([
  Object.freeze({
    question: "How do I cancel a booking?",
    answer:
      "Free cancellation up to 6 hours before your slot. Full refund is processed to the original payment method within 3 working days.",
  }),
  Object.freeze({
    question: "Do you rent equipment?",
    answer:
      "Yes — bats, rackets, shuttles, footballs, and bibs. Rental is complimentary for members and ₹100 per session otherwise.",
  }),
  Object.freeze({
    question: "Can I bring guests to my membership session?",
    answer:
      "Members get 4 free guest passes per quarter. Additional guests are charged at 50% of the hourly rate.",
  }),
  Object.freeze({
    question: "Do you host birthday parties or corporate events?",
    answer:
      "Absolutely. We have packages from ₹8,000 for 2-hour turf takeovers with catering. Contact us for a custom quote.",
  }),
  Object.freeze({
    question: "Is parking available?",
    answer:
      "Yes, 80 free covered parking spots on-site, plus valet on weekends.",
  }),
]);

export const ARENA_REFERENCE_STATS = Object.freeze([
  Object.freeze({ value: "40,000+", label: "Bookings a year" }),
  Object.freeze({ value: "12,000+", label: "Active players" }),
  Object.freeze({ value: "18", label: "Courts across 6 sports" }),
  Object.freeze({ value: "4.9★", label: "Google rating" }),
]);

export const ARENA_REFERENCE_APP = Object.freeze({
  badge: "Available now — free to download",
  title: "Book in seconds.",
  highlightedTitle: "Play instantly.",
  description:
    "The BoxArena app puts every ground, every slot, and every booking in your pocket. Real-time availability, one-tap booking, and instant confirmation on WhatsApp.",
  features: Object.freeze([
    "⚡ Instant booking",
    "📅 Slot reminders",
    "💳 UPI & card payments",
    "🔔 Real-time alerts",
    "🎟️ Digital passes",
  ]),
  proofText: "4.9 · 12,000+ happy players",
  ctaLabel: "Book a slot",
  greeting: "Good evening,",
  customerName: "Armaan 👋",
  featuredLabel: "Today's pick",
  featuredTime: "7:00 PM · 1hr",
  slotsLabel: "Available slots",
  confirmLabel: "Confirm Booking →",
  ratingText: "4.9 rating",
  reviewsText: "12k+ reviews",
  instantTitle: "Instant confirm",
  instantSubtitle: "No waiting",
});

export const ARENA_REFERENCE_GROUNDS = Object.freeze([
  Object.freeze({
    _id: "arena-reference-football",
    type: "football",
    name: "Football",
    capacity: 3,
    basePrice: 1200,
    currency: "INR",
    unit: "hr",
    referenceTagline: "5-a-side & 7-a-side turfs",
  }),
  Object.freeze({
    _id: "arena-reference-badminton",
    type: "badminton",
    name: "Badminton",
    capacity: 6,
    basePrice: 400,
    currency: "INR",
    unit: "hr",
    referenceTagline: "Wooden indoor courts",
  }),
  Object.freeze({
    _id: "arena-reference-cricket",
    type: "cricket",
    name: "Cricket",
    capacity: 2,
    basePrice: 1500,
    currency: "INR",
    unit: "hr",
    referenceTagline: "Turf pitches & practice nets",
  }),
  Object.freeze({
    _id: "arena-reference-tennis",
    type: "tennis",
    name: "Tennis",
    capacity: 2,
    basePrice: 600,
    currency: "INR",
    unit: "hr",
    referenceTagline: "Synthetic & clay courts",
  }),
  Object.freeze({
    _id: "arena-reference-pickleball",
    type: "pickleball",
    name: "Pickleball",
    capacity: 4,
    basePrice: 500,
    currency: "INR",
    unit: "hr",
    referenceTagline: "Open-air premium courts",
  }),
  Object.freeze({
    _id: "arena-reference-swimming",
    type: "swimming",
    name: "Swimming",
    capacity: 1,
    basePrice: 300,
    currency: "INR",
    unit: "hr",
    referenceTagline: "25m heated pool",
  }),
]);

export const resolveArenaText = (
  configuredValue,
  apiValue,
  fallbackValue,
) =>
  cleanText(configuredValue) ||
  cleanText(apiValue) ||
  cleanText(fallbackValue);

export const resolveArenaPositiveNumber = (
  configuredValue,
  apiValue,
  fallbackValue,
) => {
  for (const value of [configuredValue, apiValue, fallbackValue]) {
    const number = Number(value);

    if (Number.isFinite(number) && number > 0) {
      return number;
    }
  }

  return 0;
};

export const resolveArenaCollection = (
  configuredItems,
  apiItems,
  fallbackItems,
) => {
  for (const items of [configuredItems, apiItems, fallbackItems]) {
    if (Array.isArray(items) && items.length > 0) {
      return items;
    }
  }

  return [];
};

export const resolveArenaCopy = (configuredCopy = {}) =>
  Object.entries(ARENA_REFERENCE_COPY).reduce(
    (resolved, [key, fallback]) => {
      resolved[key] = resolveArenaText(
        configuredCopy?.[key],
        "",
        fallback,
      );
      return resolved;
    },
    {},
  );

export const resolveArenaApp = (configuredApp = {}) => ({
  ...configuredApp,
  ...Object.entries(ARENA_REFERENCE_APP).reduce(
    (resolved, [key, fallback]) => {
      resolved[key] = Array.isArray(fallback)
        ? resolveArenaCollection(
            configuredApp?.[key],
            [],
            fallback,
          )
        : resolveArenaText(
            configuredApp?.[key],
            "",
            fallback,
          );
      return resolved;
    },
    {},
  ),
});

const normalizeSportKey = (value) =>
  cleanText(value).toLowerCase().replace(/[^a-z0-9]+/g, "");

export const getArenaReferenceSportKey = (ground = {}) => {
  const candidates = [
    ground?.type,
    ground?.name,
    ground?.sportName,
    ground?.sport?.name,
  ].map(normalizeSportKey);

  for (const candidate of candidates) {
    if (/football|futsal|soccer/.test(candidate)) return "football";
    if (/badminton/.test(candidate)) return "badminton";
    if (/cricket/.test(candidate)) return "cricket";
    if (/pickleball/.test(candidate)) return "pickleball";
    if (/tennis/.test(candidate)) return "tennis";
    if (/swim|pool/.test(candidate)) return "swimming";
  }

  return "football";
};

export const getArenaReferenceGroundImage = (ground) =>
  ARENA_REFERENCE_IMAGES[
    getArenaReferenceSportKey(ground)
  ] || ARENA_REFERENCE_IMAGES.hero;
